import { getWeeklyCardProgress } from "./weeklyQuestCards";
import { isUnknownArray, isRecord, isTimestamp } from "../types/data";
import type { DateValue } from "../types/data";
import type { EntityId } from "../types/media";
import type { Rating } from "../types/rating";
import type { DungeonRarity, DungeonReconciliationDefinition, QuestCardCriteria, QuestRarity, WeeklyDungeonState, WeeklyQuestState } from "../types/games";
import type firebase from "firebase/compat/app";
import "firebase/compat/firestore";
import {
  generateGlobalWeeklyDungeons,
  getAllWeeklyDungeons,
  reconcileDungeonStateAfterRatingDeletion
} from "./weeklyDungeons";
import {
  getWeeklyDungeonExperienceItemId,
  getWeeklyQuestExperienceItemId
} from "./gameExperienceLedger";

const DAY_MS = 24 * 60 * 60 * 1000;

export const RATING_PROGRESS_REVISION_CONFLICT =
  "rating-progress-revision-conflict";

export class RatingProgressRevisionConflictError extends Error {
  code: string;
  expectedRevision: number | undefined;
  actualRevision: number | undefined;
  constructor({ expected, actual }: { expected?: number; actual?: number } = {}) {
    super(
      `Ratings revision changed while revoking historical progress (${expected} -> ${actual}).`
    );
    this.name = "RatingProgressRevisionConflictError";
    this.code = RATING_PROGRESS_REVISION_CONFLICT;
    this.expectedRevision = expected;
    this.actualRevision = actual;
  }
}

export const isRatingProgressRevisionConflict = (error: unknown): error is Pick<RatingProgressRevisionConflictError, "code" | "expectedRevision" | "actualRevision"> =>
  error instanceof RatingProgressRevisionConflictError ||
  (isRecord(error) && error.code === RATING_PROGRESS_REVISION_CONFLICT &&
    (error.expectedRevision === undefined || typeof error.expectedRevision === "number") &&
    (error.actualRevision === undefined || typeof error.actualRevision === "number"));

const asArray = <T>(value: T | T[] | null | undefined): T[] => isUnknownArray(value) ? value : [];

const toDate = (value: DateValue | undefined) => {
  if (!value) return null;
  const date = isTimestamp(value) ? value.toDate() : value instanceof Date ? new Date(value) :
    typeof value === "string" || typeof value === "number" ? new Date(value) : new Date(NaN);
  return Number.isNaN(date.getTime()) ? null : date;
};

const getDungeonRarity = (selectedDungeonId: string | undefined): DungeonRarity => {
  const match = String(selectedDungeonId || "").match(/_(common|epic|legendary)_/);
  return match?.[1] === "epic" ? "epic" : match?.[1] === "legendary" ? "legendary" : "common";
};

type HistoricalQuestState = Partial<Omit<WeeklyQuestState, "cards">> & { cards?: (QuestCardCriteria & { id: string; rarity?: QuestRarity })[]; expiresAt?: DateValue };
const getWeekWindow = (state: HistoricalQuestState = {}) => {
  const end = toDate(state.expiresAt);
  if (end) return { start: new Date(end.getTime() - 7 * DAY_MS), end };

  const match = String(state.weekId || "").match(/^(\d{4})-W(\d{2})$/);
  if (!match) return null;
  const year = Number(match[1]);
  const week = Number(match[2]);
  const jan4 = new Date(year, 0, 4);
  const jan4Day = jan4.getDay() || 7;
  const start = new Date(year, 0, 4 - jan4Day + 1 + (week - 1) * 7);
  start.setHours(0, 0, 0, 0);
  const weekEnd = new Date(start);
  weekEnd.setDate(start.getDate() + 7);
  return { start, end: weekEnd };
};

export const buildHistoricalDungeonRevocation = (
  state: WeeklyDungeonState = {},
  filmId: EntityId,
  now = Date.now(),
  {
    dungeon = null,
    progressRatings = null
  }: { dungeon?: DungeonReconciliationDefinition | null; progressRatings?: Rating[] | null } = {}
): { wasCompleted: boolean; rarity: DungeonRarity; state: WeeklyDungeonState } | null => {
  if (state.failureReason === "rating_deleted" || state.revokedAt) return null;
  const targetId = String(filmId || "");
  const stageIndex = asArray(state.stageProgress).findIndex((stage) =>
    stage?.status === "completed" &&
    asArray(stage.matchedMovieIds).map(String).includes(targetId)
  );
  if (stageIndex < 0) return null;

  if (dungeon && isUnknownArray(progressRatings)) {
    const reconciliation = reconcileDungeonStateAfterRatingDeletion({
      state,
      dungeon,
      ratings: progressRatings,
      now
    });
    if (!reconciliation?.changed) return null;
    return {
      wasCompleted: state.status === "completed" && reconciliation.state.status !== "completed",
      rarity: getDungeonRarity(state.selectedDungeonId),
      state: reconciliation.state
    };
  }

  const wasCompleted = state.status === "completed";
  return {
    wasCompleted,
    rarity: getDungeonRarity(state.selectedDungeonId),
    state: {
      ...state,
      status: "failed",
      failedAt: now,
      revokedAt: now,
      failureReason: "rating_deleted",
      revokedMovieIds: [
        ...new Set([...asArray(state.revokedMovieIds).map(String), targetId])
      ],
      updatedAt: now,
      stageProgress: asArray(state.stageProgress).map((stage, index) =>
        index === stageIndex || stage?.status === "in_progress"
          ? { ...stage, status: "failed", revokedAt: now }
          : stage
      )
    }
  };
};

export const buildHistoricalWeeklyQuestRevocation = (
  state: HistoricalQuestState = {},
  progressRatings: Rating[] = [],
  now = Date.now()
) => {
  const persistedIds = [...new Set([
    ...asArray(state.completedQuestIds),
    ...asArray(state.rewardedQuestIds)
  ])];
  if (!persistedIds.length) return null;
  const window = getWeekWindow(state);
  if (!window) return null;

  const cardsById = new Map(asArray(state.cards).map((card) => [card?.id, card]));
  const revokedIds = persistedIds.filter((cardId) => {
    const card = cardsById.get(cardId);
    if (!card) return true;
    return getWeeklyCardProgress(
      card,
      progressRatings,
      state.selectedAtByCardId?.[cardId],
      window.start,
      window.end
    ).progress < 1;
  });
  if (!revokedIds.length) return null;

  const revokedSet = new Set(revokedIds);
  const rewardedSet = new Set(asArray(state.rewardedQuestIds));
  return {
    revokedIds,
    revokedRewardedIds: revokedIds.filter((id) => rewardedSet.has(id)),
    state: {
      ...state,
      completedQuestIds: asArray(state.completedQuestIds)
        .filter((id) => !revokedSet.has(id)),
      rewardedQuestIds: asArray(state.rewardedQuestIds)
        .filter((id) => !revokedSet.has(id)),
      completedAtByCardId: Object.fromEntries(
        Object.entries(state.completedAtByCardId || {})
          .filter(([id]) => !revokedSet.has(id))
      ),
      updatedAt: new Date(now)
    }
  };
};

export const countLegendaryWeeklyQuestCompletions = (states: { cards?: { id: string; rarity?: QuestRarity }[]; rewardedQuestIds?: string[] }[] = []) =>
  asArray(states).reduce((total, state) => {
    const rewarded = new Set(asArray(state.rewardedQuestIds));
    return total + asArray(state.cards).filter(
      (card) => card?.rarity === "legendary" && rewarded.has(card.id)
    ).length;
  }, 0);

const getSnapshotDocs = (snapshot: firebase.firestore.QuerySnapshot | null | undefined) => asArray(snapshot?.docs);

interface DungeonCompletionStats { completed?: number; epicCompleted?: number; legendaryCompleted?: number; }
interface ProgressUserData { ratingsSync?: { revision?: number }; weeklyDungeonStats?: DungeonCompletionStats; }
const getRatingsRevision = (snapshot: firebase.firestore.DocumentSnapshot | null | undefined) => {
  const data: ProgressUserData | undefined = typeof snapshot?.data === "function" ? snapshot.data() : {};
  const revision = Number(data?.ratingsSync?.revision);
  return Number.isInteger(revision) && revision > 0 ? revision : 0;
};

// Collection-specific schema is supplied at the Firestore deserialization boundary.
const getDocState = <T extends { weekId?: string }>(snapshot: firebase.firestore.DocumentSnapshot, fallbackWeekId = ""): T & { weekId: string } => {
  const data = snapshot?.data?.() as T | undefined;
  return { ...(data || {} as T), weekId: data?.weekId || snapshot?.id || fallbackWeekId };
};
const getDungeonDocState = (snapshot: firebase.firestore.DocumentSnapshot) => getDocState<WeeklyDungeonState>(snapshot);
const getQuestDocState = (snapshot: firebase.firestore.DocumentSnapshot) => getDocState<HistoricalQuestState>(snapshot);

const getDungeonForState = (state: WeeklyDungeonState = {}) => {
  if (!state.weekId || !state.selectedDungeonId) return null;
  const globalDungeons = generateGlobalWeeklyDungeons(state.weekId);
  return getAllWeeklyDungeons(globalDungeons)
    .find((dungeon) => dungeon.id === state.selectedDungeonId) || null;
};

const getReconciledQuestStates = (snapshot: firebase.firestore.QuerySnapshot, progressRatings: Rating[]) =>
  getSnapshotDocs(snapshot).map((item) => {
    const state = getQuestDocState(item);
    return buildHistoricalWeeklyQuestRevocation(state, progressRatings)?.state || state;
  });

const getReconciledDungeonStates = (snapshot: firebase.firestore.QuerySnapshot, filmId: EntityId, progressRatings: Rating[]) =>
  getSnapshotDocs(snapshot).map((item) => {
    const state = getDungeonDocState(item);
    return buildHistoricalDungeonRevocation(state, filmId, Date.now(), {
      dungeon: getDungeonForState(state),
      progressRatings
    })?.state || state;
  });

type QuestRevocation = NonNullable<ReturnType<typeof buildHistoricalWeeklyQuestRevocation>>;
type DungeonRevocation = NonNullable<ReturnType<typeof buildHistoricalDungeonRevocation>>;
const getRevokedQuestExperienceItemIds = (state: HistoricalQuestState, revocation: QuestRevocation | null | undefined) =>
  asArray(revocation?.revokedRewardedIds).map((cardId) =>
    getWeeklyQuestExperienceItemId(state.weekId || "", cardId)
  );

const getRevokedDungeonExperienceItemIds = (state: WeeklyDungeonState, revocation: DungeonRevocation | null | undefined) =>
  revocation?.wasCompleted
    ? [getWeeklyDungeonExperienceItemId(state.weekId || "", state.selectedDungeonId || "")]
    : [];

const buildHistoricalRatingProgressPreview = ({
  dungeonSnapshot,
  questSnapshot,
  userSnapshot,
  filmId,
  progressRatings
}: { dungeonSnapshot: firebase.firestore.QuerySnapshot; questSnapshot: firebase.firestore.QuerySnapshot; userSnapshot: firebase.firestore.DocumentSnapshot; filmId: EntityId; progressRatings: Rating[] }) => {
  const dungeonStates: WeeklyDungeonState[] = [];
  const questStates: HistoricalQuestState[] = [];
  const revokedExperienceItemIds: string[] = [];
  const decrements = { completed: 0, epicCompleted: 0, legendaryCompleted: 0 };
  const now = Date.now();

  getSnapshotDocs(dungeonSnapshot).forEach((item) => {
    const state = getDungeonDocState(item);
    const revocation = buildHistoricalDungeonRevocation(state, filmId, now, {
      dungeon: getDungeonForState(state),
      progressRatings
    });
    dungeonStates.push(revocation?.state || state);
    revokedExperienceItemIds.push(
      ...getRevokedDungeonExperienceItemIds(state, revocation)
    );
    if (!revocation?.wasCompleted) return;
    decrements.completed += 1;
    if (revocation.rarity === "epic") decrements.epicCompleted += 1;
    if (revocation.rarity === "legendary") decrements.legendaryCompleted += 1;
  });

  getSnapshotDocs(questSnapshot).forEach((item) => {
    const state = getQuestDocState(item);
    const revocation = buildHistoricalWeeklyQuestRevocation(
      state,
      progressRatings,
      now
    );
    questStates.push(revocation?.state || state);
    revokedExperienceItemIds.push(
      ...getRevokedQuestExperienceItemIds(state, revocation)
    );
  });

  const currentDungeonStats: DungeonCompletionStats = userSnapshot?.data?.()?.weeklyDungeonStats || {};
  const dungeonStats = decrements.completed > 0
    ? {
      completed: Math.max(
        0,
        (Number(currentDungeonStats.completed) || 0) - decrements.completed
      ),
      epicCompleted: Math.max(
        0,
        (Number(currentDungeonStats.epicCompleted) || 0) - decrements.epicCompleted
      ),
      legendaryCompleted: Math.max(
        0,
        (Number(currentDungeonStats.legendaryCompleted) || 0) -
          decrements.legendaryCompleted
      )
    }
    : null;

  return {
    dungeonStats,
    legendaryQuestCardsCompleted: countLegendaryWeeklyQuestCompletions(questStates),
    weeklyQuestStates: questStates,
    weeklyDungeonStates: dungeonStates,
    revokedExperienceItemIds: [...new Set(revokedExperienceItemIds)]
  };
};

// The canonical rating deletion needs its exact signed XP payload before any
// write is committed. This preview shares the same reconciliation functions as
// the persistent path but performs reads only, so a failed delete cannot revoke
// progress on its own.
interface HistoricalRevocationOptions {
  firestore?: firebase.firestore.Firestore | null;
  userId?: string | null;
  filmId?: EntityId | null;
  progressRatings?: Rating[];
  currentWeekId?: string;
  expectedRatingsRevision?: number | null;
}

export const previewHistoricalRatingProgressRevocation = async ({
  firestore,
  userId,
  filmId,
  progressRatings = []
}: HistoricalRevocationOptions = {}) => {
  if (!firestore || !userId || filmId == null) return null;
  const userRef = firestore.collection("users").doc(userId);
  const dungeonCollection = userRef.collection("weeklyDungeonState");
  const questCollection = userRef.collection("weeklyQuests");
  if (
    typeof userRef?.get !== "function" ||
    typeof dungeonCollection?.get !== "function" ||
    typeof questCollection?.get !== "function"
  ) {
    return null;
  }

  const [dungeonSnapshot, questSnapshot, userSnapshot] = await Promise.all([
    dungeonCollection.get(),
    questCollection.get(),
    userRef.get()
  ]);
  return buildHistoricalRatingProgressPreview({
    dungeonSnapshot,
    questSnapshot,
    userSnapshot,
    filmId,
    progressRatings
  });
};

// Deletion is rare, so reconciling historical weekly documents here is much
// cheaper and more reliable than keeping listeners open for every past week.
export const revokeHistoricalRatingProgress = async ({
  firestore,
  userId,
  filmId,
  currentWeekId,
  progressRatings = [],
  expectedRatingsRevision = null
}: HistoricalRevocationOptions = {}) => {
  if (!firestore || !userId || filmId == null || typeof firestore.runTransaction !== "function") {
    return null;
  }

  const userRef = firestore.collection("users").doc(userId);
  const dungeonCollection = userRef.collection("weeklyDungeonState");
  const questCollection = userRef.collection("weeklyQuests");
  if (typeof dungeonCollection?.get !== "function" || typeof questCollection?.get !== "function") {
    return null;
  }

  const [dungeonSnapshot, questSnapshot] = await Promise.all([
    dungeonCollection.get(),
    questCollection.get()
  ]);

  const dungeonRevocations = new Map<string, DungeonRevocation>();
  getSnapshotDocs(dungeonSnapshot).forEach((item) => {
    const state = getDungeonDocState(item);
    const revocation = buildHistoricalDungeonRevocation(state, filmId, Date.now(), {
      dungeon: getDungeonForState(getDungeonDocState(item)),
      progressRatings
    });
    if (revocation) dungeonRevocations.set(item.id, revocation);
  });
  const questRevocations = new Map<string, QuestRevocation>();
  getSnapshotDocs(questSnapshot).forEach((item) => {
    const revocation = buildHistoricalWeeklyQuestRevocation(
      getQuestDocState(item),
      progressRatings
    );
    if (revocation) questRevocations.set(item.id, revocation);
  });

  const historicalDungeonDocs = getSnapshotDocs(dungeonSnapshot).filter((item) =>
    item.id !== currentWeekId && dungeonRevocations.has(item.id)
  );
  const historicalQuestDocs = getSnapshotDocs(questSnapshot).filter((item) =>
    item.id !== currentWeekId && questRevocations.has(item.id)
  );
  const currentRevokedExperienceItemIds = [
    ...getSnapshotDocs(dungeonSnapshot).flatMap((item) => {
      if (item.id !== currentWeekId) return [];
      return getRevokedDungeonExperienceItemIds(
        getDungeonDocState(item),
        dungeonRevocations.get(item.id)
      );
    }),
    ...getSnapshotDocs(questSnapshot).flatMap((item) => {
      if (item.id !== currentWeekId) return [];
      return getRevokedQuestExperienceItemIds(
        getQuestDocState(item),
        questRevocations.get(item.id)
      );
    })
  ];

  const candidateDocs = [...historicalDungeonDocs, ...historicalQuestDocs];
  const buildNoHistoricalWritesResult = () => ({
    dungeonStats: null,
    legendaryQuestCardsCompleted: countLegendaryWeeklyQuestCompletions(
      getReconciledQuestStates(questSnapshot, progressRatings)
    ),
    weeklyQuestStates: getReconciledQuestStates(questSnapshot, progressRatings),
    weeklyDungeonStates: getReconciledDungeonStates(
      dungeonSnapshot,
      filmId,
      progressRatings
    ),
    revokedExperienceItemIds: [...new Set(currentRevokedExperienceItemIds)]
  });
  const shouldCheckRatingsRevision = expectedRatingsRevision != null;
  const normalizedExpectedRatingsRevision = Number(expectedRatingsRevision);
  if (
    shouldCheckRatingsRevision &&
    (!Number.isInteger(normalizedExpectedRatingsRevision) ||
      normalizedExpectedRatingsRevision < 0)
  ) {
    throw new TypeError("expectedRatingsRevision must be a non-negative integer");
  }
  if (!candidateDocs.length && !shouldCheckRatingsRevision) {
    return buildNoHistoricalWritesResult();
  }

  return firestore.runTransaction(async (transaction) => {
    const [candidateSnapshots, userSnapshot] = await Promise.all([
      Promise.all(candidateDocs.map((item) => transaction.get(item.ref))),
      transaction.get(userRef)
    ]);
    if (shouldCheckRatingsRevision) {
      const actualRatingsRevision = getRatingsRevision(userSnapshot);
      if (actualRatingsRevision !== normalizedExpectedRatingsRevision) {
        throw new RatingProgressRevisionConflictError({
          expected: normalizedExpectedRatingsRevision,
          actual: actualRatingsRevision
        });
      }
    }
    if (!candidateDocs.length) return buildNoHistoricalWritesResult();
    const dungeonRefs = new Set(historicalDungeonDocs.map((item) => item.ref));
    const nextQuestStateById = new Map<string, HistoricalQuestState>(
      getSnapshotDocs(questSnapshot).map((item) => {
        const state = getQuestDocState(item);
        return [
          item.id,
          buildHistoricalWeeklyQuestRevocation(state, progressRatings)?.state || state
        ];
      })
    );
    const nextDungeonStateById = new Map<string, WeeklyDungeonState>(
      getSnapshotDocs(dungeonSnapshot).map((item) => [item.id, getDungeonDocState(item)])
    );
    const decrements = { completed: 0, epicCompleted: 0, legendaryCompleted: 0 };
    const revokedExperienceItemIds = [...currentRevokedExperienceItemIds];

    candidateSnapshots.forEach((snapshot) => {
      if (dungeonRefs.has(snapshot.ref)) {
        const state = getDungeonDocState(snapshot);
        const revocation = buildHistoricalDungeonRevocation(state, filmId, Date.now(), {
          dungeon: getDungeonForState(state),
          progressRatings
        });
        if (!revocation) return;
        transaction.set(snapshot.ref, revocation.state, { merge: true });
        nextDungeonStateById.set(snapshot.id, revocation.state);
        revokedExperienceItemIds.push(
          ...getRevokedDungeonExperienceItemIds(state, revocation)
        );
        if (revocation.wasCompleted) {
          decrements.completed += 1;
          if (revocation.rarity === "epic") decrements.epicCompleted += 1;
          if (revocation.rarity === "legendary") decrements.legendaryCompleted += 1;
        }
        return;
      }

      const state = getQuestDocState(snapshot);
      const revocation = buildHistoricalWeeklyQuestRevocation(state, progressRatings);
      if (!revocation) return;
      transaction.set(snapshot.ref, revocation.state, { merge: true });
      nextQuestStateById.set(snapshot.id, revocation.state);
      revokedExperienceItemIds.push(
        ...getRevokedQuestExperienceItemIds(state, revocation)
      );
    });

    let dungeonStats: DungeonCompletionStats | null = null;
    if (decrements.completed > 0) {
      const current: DungeonCompletionStats = userSnapshot?.data?.()?.weeklyDungeonStats || {};
      dungeonStats = {
        completed: Math.max(0, (Number(current.completed) || 0) - decrements.completed),
        epicCompleted: Math.max(0, (Number(current.epicCompleted) || 0) - decrements.epicCompleted),
        legendaryCompleted: Math.max(
          0,
          (Number(current.legendaryCompleted) || 0) - decrements.legendaryCompleted
        )
      };
      transaction.set(userRef, { weeklyDungeonStats: dungeonStats }, { merge: true });
    }

    return {
      dungeonStats,
      legendaryQuestCardsCompleted: countLegendaryWeeklyQuestCompletions(
        [...nextQuestStateById.values()]
      ),
      weeklyQuestStates: [...nextQuestStateById.values()],
      weeklyDungeonStates: [...nextDungeonStateById.values()],
      revokedExperienceItemIds: [...new Set(revokedExperienceItemIds)]
    };
  });
};
